/**
 * Calendar Sync Panel (Phase 4 — Steps 4.4, 4.5).
 *
 * UI for connecting and syncing with external calendars.
 */

import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { CollapsiblePanel, Button } from '../shared';
import {
  type CalendarProvider,
  type CalendarSyncConfig,
  DEFAULT_SYNC_CONFIG,
  getAuthUrl,
  generateICSFile,
} from '@/integration/calendarSync';
import { downloadICS } from '@/export/icsExport';

export function CalendarSyncPanel(): React.ReactElement {
  const [config, setConfig] = useState<CalendarSyncConfig>(DEFAULT_SYNC_CONFIG);
  const [status, setStatus] = useState<string>('');

  const handleConnect = (): void => {
    const url = getAuthUrl(config.provider);
    if (url) {
      // In production, redirect to OAuth consent screen
      setStatus(`Redirecting to ${config.provider} for authorization... (stub)`);
    } else {
      setStatus(`${config.provider} calendar sync is not yet available.`);
    }
  };

  const handleExportICS = (): void => {
    // Generate a sample .ics file for the user to import manually
    const icsContent = generateICSFile([]);
    downloadICS(icsContent, 'absence-budget-sessions.ics');
    setStatus('Downloaded .ics file. Import it into your calendar app.');
  };

  return (
    <CollapsiblePanel title="Calendar Sync" defaultOpen={false}>
      <div className="calendar-sync-panel">
        <div className="calendar-sync-panel__provider">
          <label>
            Calendar Provider
            <select
              value={config.provider}
              onChange={(e) => setConfig({ ...config, provider: e.target.value as CalendarProvider })}
            >
              <option value="google">Google Calendar</option>
              <option value="apple">Apple Calendar (.ics)</option>
              <option value="outlook">Outlook</option>
            </select>
          </label>
        </div>

        {config.provider === 'apple' ? (
          <div className="calendar-sync-panel__ics">
            <p>Apple Calendar uses .ics files. Download and import manually.</p>
            <Button variant="secondary" size="sm" onClick={handleExportICS}>Download .ics</Button>
          </div>
        ) : (
          <>
            <div className="calendar-sync-panel__auth">
              {config.accessToken ? (
                <p className="calendar-sync-panel__connected"><Check aria-hidden="true" /> Connected</p>
              ) : (
                <Button variant="primary" size="sm" onClick={handleConnect}>
                  Connect {config.provider === 'google' ? 'Google' : 'Outlook'} Calendar
                </Button>
              )}
            </div>

            <div className="calendar-sync-panel__options">
              <label>
                Sync mode
                <select
                  value={config.syncMode}
                  onChange={(e) => setConfig({ ...config, syncMode: e.target.value as CalendarSyncConfig['syncMode'] })}
                >
                  <option value="all">All sessions + absences</option>
                  <option value="absences_only">Absences only</option>
                  <option value="sessions_only">Sessions only</option>
                </select>
              </label>
              <label className="calendar-sync-panel__toggle">
                <input
                  type="checkbox"
                  checked={config.autoSync}
                  onChange={(e) => setConfig({ ...config, autoSync: e.target.checked })}
                />
                Auto-sync when absences are logged
              </label>
            </div>
          </>
        )}

        {status && <p className="calendar-sync-panel__status">{status}</p>}
      </div>
    </CollapsiblePanel>
  );
}
